/**
 * Planificación: convierte un objetivo o la disponibilidad de la semana en propuestas que
 * el usuario revisa. Nunca guarda nada: devuelve elementos (`Proposal`) más la explicación
 * de cada decisión y las advertencias.
 *
 * Límites que se explican en la propia propuesta:
 * - Solo se comprueban conflictos con lo que HabitOS conoce (tareas con hora y
 *   recordatorios). No hay acceso a calendarios externos.
 * - HabitOS no guarda la duración de las tareas: se reservan 30 minutos por tarea.
 */
import { z } from 'zod';
import { addDays, isoWeekday, type LocalDate } from '@habitos/engine';
import type { Ctx } from '../domain/data';
import type { HabitRec } from '../domain/model';
import { makeT } from '../i18n/core';
import type { AppData } from '../domain/data';
import { windowSchema, type Window } from './intents';
import type { Proposal } from './interpreter';
import { activeHabits, openTasks } from './insights';
import { matchHabits } from './deterministic';
import { addMinutes, ALL_DAYS, dayNames, formatDay, simplify, spreadDays, toMinutes } from './normalize';
import { tr, type TextRef } from './parts';
import { preferredSlot, withinLimits, type Preferences } from './preferences';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const day = z.number().int().min(1).max(7);

export const goalDraftSchema = z.object({
  kind: z.literal('goal'),
  activity: z.string().trim().min(1).max(80),
  minutes: z.number().int().min(1).max(600).nullable(),
  days: z.array(day).min(1).max(7),
  except: z.array(day).max(6),
  quota: z.number().int().min(1).max(6).nullable(),
  time: hhmm.nullable(),
  /** La hora salió de una franja ("por la tarde"): se explica que es una sugerencia. */
  timeFromSlot: z.boolean(),
  /** Total semanal pedido ("seis horas por semana"): se reparte en sesiones en días fijos. */
  weeklyMinutes: z.number().int().min(15).max(3000).nullable().default(null),
  /** Días que deben quedar libres. */
  freeDays: z.array(day).max(6).default([]),
  /** Alternativa mostrada (0 = la primera). */
  variant: z.number().int().min(0).max(50).default(0),
});
export type GoalDraft = z.infer<typeof goalDraftSchema>;

export const routineActivitySchema = z.object({
  name: z.string().trim().min(1).max(80),
  minutes: z.number().int().min(5).max(600).nullable(),
  /** Días pedidos explícitamente; si no, se reparten. */
  days: z.array(day).min(1).max(7).nullable(),
  sessions: z.number().int().min(1).max(7).nullable(),
  weeklyMinutes: z.number().int().min(15).max(3000).nullable(),
  time: hhmm.nullable(),
});
export type RoutineActivity = z.infer<typeof routineActivitySchema>;

export const routineDraftSchema = z.object({
  kind: z.literal('routine'),
  activities: z.array(routineActivitySchema).min(1).max(6),
  freeDays: z.array(day).max(6),
  windows: z.array(windowSchema).max(14).nullable(),
  variant: z.number().int().min(0).max(50),
});
export type RoutineDraft = z.infer<typeof routineDraftSchema>;

export const weekDraftSchema = z.object({
  kind: z.literal('week'),
  windows: z.array(windowSchema).min(1).max(14),
  assumedAllDays: z.boolean(),
  /** Tareas que el usuario quitó de la propuesta. */
  removed: z.array(z.string().max(64)).max(200),
  /** Fecha y hora fijadas por el usuario para una tarea ("mueve X al jueves"). */
  overrides: z.record(z.object({ date: localDate, time: hhmm.nullable() })),
  /** Alternativa: las impares colocan las tareas lo más tarde posible dentro de la semana. */
  variant: z.number().int().min(0).max(50).default(0),
});
export type WeekDraft = z.infer<typeof weekDraftSchema>;

export const draftSchema = z.discriminatedUnion('kind', [goalDraftSchema, weekDraftSchema, routineDraftSchema, z.object({ kind: z.literal('items') })]);
export type Draft = z.infer<typeof draftSchema>;

export interface PlanEnv {
  data: AppData;
  ctx: Ctx;
  locale: 'es' | 'en';
  now: number;
  /** Preferencias recordadas: franja por actividad, límites de hora y días libres. */
  prefs?: Preferences;
}

export interface Plan {
  items: Proposal[];
  explain: TextRef[];
  warnings: TextRef[];
}

/** Bloque reservado por defecto para una tarea, porque su duración no se conoce. */
export const TASK_BLOCK_MINUTES = 30;
const HORIZON_DAYS = 14;

export interface Busy {
  date: LocalDate;
  time: string;
  name: string;
}

/** Horas ya ocupadas según HabitOS: tareas con hora y recordatorios activos de hábitos vigentes. */
export function busyOn(data: AppData, date: LocalDate, excludeHabitId: string | null = null): Busy[] {
  const out: Busy[] = [];
  for (const h of openTasks(data)) if (h.dueDate === date && h.dueTime) out.push({ date, time: h.dueTime, name: h.name });
  const live = new Map<string, HabitRec>(data.habits.filter((h) => !h.deletedAt && !h.archivedAt && !h.completedAt).map((h) => [h.id, h]));
  const wd = isoWeekday(date);
  for (const r of data.reminders) {
    if (r.deletedAt || !r.enabled || r.habitId === excludeHabitId) continue;
    const h = live.get(r.habitId);
    if (!h || (h.kind === 'task' && h.dueDate !== date)) continue;
    if (r.days && r.days.length && !r.days.includes(wd)) continue;
    if (h.kind === 'task' && h.dueTime === r.time) continue;
    out.push({ date, time: r.time, name: h.name });
  }
  return out.sort((a, b) => a.time.localeCompare(b.time));
}

/** Elementos con hora dentro de [start, start + minutos). */
export function conflictsIn(busy: Busy[], start: string, minutes: number): Busy[] {
  const a = toMinutes(start);
  const b = a + Math.max(minutes, 1);
  return busy.filter((x) => toMinutes(x.time) >= a && toMinutes(x.time) < b);
}

/** Hora actual (HH:MM) en la zona horaria del usuario. */
export function nowHHMM(now: number, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(now);
}

// --- Objetivo → hábito -------------------------------------------------------------------

/** Sesiones para un total semanal: de 45 a 120 min, entre 2 y 6 por semana, priorizando 90 y 60 min. */
export function sessionsFor(weeklyMinutes: number, minutes: number | null, maxDays = 7): { sessions: number; minutes: number } {
  if (minutes) return { sessions: Math.max(1, Math.min(maxDays, Math.round(weeklyMinutes / minutes))), minutes };
  for (const m of [90, 60, 120, 45]) {
    const n = weeklyMinutes / m;
    if (Number.isInteger(n) && n >= 2 && n <= Math.min(6, maxDays)) return { sessions: n, minutes: m };
  }
  const sessions = Math.max(1, Math.min(maxDays, Math.round(weeklyMinutes / 60)));
  return { sessions, minutes: Math.round(weeklyMinutes / sessions / 5) * 5 };
}

const hoursText = (min: number, locale: 'es' | 'en') =>
  min % 60 === 0 ? `${min / 60} ${locale === 'en' ? (min === 60 ? 'hour' : 'hours') : min === 60 ? 'hora' : 'horas'}` : `${min} min`;

export function buildGoalPlan(d: GoalDraft, env: PlanEnv): Plan {
  const { data, ctx, locale } = env;
  const fmt = (x: string) => formatDay(x, locale, { weekday: 'short', day: 'numeric', month: 'short' });
  const allowed = ALL_DAYS.filter((x) => !(d.freeDays ?? []).includes(x));
  let days = (d.days.length ? d.days : ALL_DAYS).filter((x) => allowed.includes(x));
  let minutes = d.minutes;
  let weekly: { sessions: number; minutes: number } | null = null;
  if (d.weeklyMinutes) {
    weekly = sessionsFor(d.weeklyMinutes, d.minutes, allowed.length);
    minutes = weekly.minutes;
    days = spreadDays(weekly.sessions, d.days.length < 7 ? days : allowed, d.variant ?? 0);
  }
  if (!days.length) days = allowed;
  const schedule: Extract<Proposal, { kind: 'create_habit' }>['schedule'] =
    d.quota !== null && !weekly ? { type: 'weekly_quota', mode: 'times', times: d.quota } : days.length === 7 ? { type: 'daily' } : { type: 'weekdays', days };
  const item: Proposal = {
    kind: 'create_habit',
    name: d.activity,
    habitKind: 'build',
    schedule,
    goalType: minutes ? 'numeric' : 'binary',
    target: minutes ?? 1,
    unit: minutes ? 'min' : null,
    reminder: d.time ? { time: d.time, days: schedule.type === 'weekdays' ? days : null } : null,
  };

  const explain: TextRef[] = [tr('chat.plan.activity', { name: d.activity })];
  if (weekly) {
    explain.push(tr('chat.plan.weekly', { total: hoursText(d.weeklyMinutes!, locale), sessions: weekly.sessions, minutes: weekly.minutes }));
    explain.push(tr('chat.plan.days', { days: dayNames(days, locale) }));
  } else if (d.quota !== null) explain.push(tr('chat.plan.quota', { count: d.quota }));
  else if (d.except.length) explain.push(tr('chat.plan.daysExcept', { days: dayNames(days, locale), except: dayNames(d.except, locale) }));
  else if (days.length === 7) explain.push(tr('chat.plan.daily'));
  else explain.push(tr('chat.plan.days', { days: dayNames(days, locale) }));
  if ((d.freeDays ?? []).length) explain.push(tr('chat.plan.freeDays', { days: dayNames(d.freeDays, locale) }));
  if (!weekly) explain.push(minutes ? tr('chat.plan.minutes', { minutes }) : tr('chat.plan.noMinutes'));

  const warnings: TextRef[] = [];
  if (d.time) {
    explain.push(tr(d.timeFromSlot ? 'chat.plan.timeFromSlot' : 'chat.plan.time', { time: d.time, end: addMinutes(d.time, minutes ?? 15) }));
    const found: Busy[] = [];
    for (let i = 0; i < HORIZON_DAYS; i++) {
      const date = addDays(ctx.today, i);
      if (schedule.type !== 'weekly_quota' && !days.includes(isoWeekday(date))) continue;
      found.push(...conflictsIn(busyOn(data, date), d.time, minutes ?? 15));
    }
    if (found.length) {
      warnings.push(tr('chat.plan.conflicts', { count: found.length, days: HORIZON_DAYS }));
      for (const c of found.slice(0, 4)) warnings.push(tr('chat.plan.conflict', { date: fmt(c.date), time: c.time, name: c.name }));
    } else explain.push(tr('chat.plan.noConflicts', { days: HORIZON_DAYS }));
    warnings.push(tr('chat.plan.agendaScope'));
    warnings.push(tr('chat.plan.reminderNote'));
  } else {
    explain.push(tr('chat.plan.noTime'));
  }
  const same = activeHabits(data).find((h) => simplify(h.name) === simplify(d.activity));
  if (same) warnings.unshift(tr('chat.plan.duplicate', { name: same.name }));
  return { items: [item], explain, warnings };
}

/** Horas que se prueban al buscar otra alternativa o un hueco libre, por orden de preferencia. */
const CANDIDATE_TIMES = ['18:00', '19:00', '08:00', '20:00', '07:00', '13:00', '21:00', '17:00', '09:00'];

/** Conflictos de una hora en los días de un horario durante el horizonte. */
function conflictCount(env: PlanEnv, days: number[] | null, time: string, minutes: number, extra: Busy[] = []): number {
  let n = 0;
  for (let i = 0; i < HORIZON_DAYS; i++) {
    const date = addDays(env.ctx.today, i);
    if (days && !days.includes(isoWeekday(date))) continue;
    n += conflictsIn([...busyOn(env.data, date), ...extra.filter((b) => b.date === date)], time, minutes).length;
  }
  return n;
}

/**
 * Otra versión de un objetivo. Por orden: cuota flexible → días fijos repartidos; días
 * repartidos → otro reparto; con hora → la siguiente hora sin choques. null si no hay otra.
 */
export function alternativeGoal(d: GoalDraft, env: PlanEnv): { draft: GoalDraft; change: TextRef } | null {
  const allowed = ALL_DAYS.filter((x) => !(d.freeDays ?? []).includes(x));
  const variant = (d.variant ?? 0) + 1;
  if (d.quota !== null && !d.weeklyMinutes) {
    const days = spreadDays(d.quota, allowed, variant - 1);
    return { draft: { ...d, quota: null, days, except: [], variant }, change: tr('chat.p.altFixedDays', { days: dayNames(days, env.locale) }) };
  }
  if (d.weeklyMinutes) {
    const next = { ...d, variant };
    const plan = buildGoalPlan(next, env);
    const item = plan.items[0];
    const days = item?.kind === 'create_habit' && item.schedule.type === 'weekdays' ? item.schedule.days : null;
    const before = buildGoalPlan(d, env).items[0];
    const prevDays = before?.kind === 'create_habit' && before.schedule.type === 'weekdays' ? before.schedule.days : null;
    if (days && JSON.stringify(days) !== JSON.stringify(prevDays)) return { draft: next, change: tr('chat.p.altFixedDays', { days: dayNames(days, env.locale) }) };
  }
  const minutes = d.minutes ?? 30;
  const days = d.days.length === 7 ? null : d.days;
  const tried = CANDIDATE_TIMES.filter((t) => t !== d.time && withinLimits(env.prefs, t, minutes));
  const offset = (d.variant ?? 0) % tried.length;
  const ordered = [...tried.slice(offset), ...tried.slice(0, offset)];
  const free = ordered.find((t) => conflictCount(env, days, t, minutes) === 0);
  if (!free) return null;
  return { draft: { ...d, time: free, timeFromSlot: false, variant }, change: tr('chat.p.altTime', { time: free }) };
}

// --- Rutina con varias actividades ------------------------------------------------------------

export const SYNONYMS = [
  ['leer', 'lectura', 'libro', 'libros'],
  ['entrenar', 'entrenamiento', 'entreno', 'gimnasio', 'gym', 'ejercicio', 'pesas'],
  ['estudiar', 'estudio', 'estudios', 'repasar'],
  ['meditar', 'meditacion', 'mindfulness'],
  ['correr', 'running', 'trotar'],
  ['caminar', 'caminata', 'pasear', 'paseo'],
  ['cocinar', 'cocina'],
  ['escribir', 'escritura'],
];

/**
 * Clave estable de una actividad para recordar preferencias: el primer sinónimo de su grupo
 * ("entrenamiento", "gimnasio" → "entrenar") o la primera palabra significativa.
 */
export function activityKey(name: string): string {
  const words = simplify(name).split(' ').filter((w) => w.length >= 3);
  for (const w of words) {
    const g = SYNONYMS.find((group) => group.includes(w));
    if (g) return g[0]!;
  }
  return words[0] ?? simplify(name);
}

/** Hábitos que se refieren a la misma actividad ("lectura" ↔ "Leer", "entrenamiento" ↔ "Entrenar"). */
export function relatedHabits(text: string, pool: HabitRec[]): HabitRec[] {
  const ctxHabits = pool.map((h) => ({ id: h.id, name: h.name, kind: h.kind, goalType: 'binary' as const, unit: null, defaultIncrement: 1, archived: false, completed: false }));
  const direct = matchHabits(text, ctxHabits);
  if (direct.length) return direct.map((x) => pool.find((h) => h.id === x.id)!);
  const words = simplify(text).split(' ').filter((w) => w.length >= 4);
  const group = (w: string) => SYNONYMS.findIndex((g) => g.includes(w));
  return pool.filter((h) =>
    simplify(h.name)
      .split(' ')
      .filter((w) => w.length >= 4)
      .some((hw) => words.some((w) => (group(w) >= 0 && group(w) === group(hw)) || (w.length >= 5 && hw.length >= 5 && w.slice(0, 5) === hw.slice(0, 5)))),
  );
}

/** Valores iniciales razonables por tipo de actividad; se explican como sugerencia. */
function defaultsFor(name: string): { sessions: number; minutes: number; time: string } {
  const n = simplify(name);
  const is = (i: number) => SYNONYMS[i]!.some((w) => n.split(' ').includes(w));
  if (is(2)) return { sessions: 5, minutes: 60, time: '18:00' };
  if (is(1)) return { sessions: 3, minutes: 60, time: '19:00' };
  if (is(4)) return { sessions: 3, minutes: 30, time: '08:00' };
  if (is(0)) return { sessions: 7, minutes: 20, time: '21:00' };
  if (is(3)) return { sessions: 7, minutes: 10, time: '08:00' };
  if (is(5)) return { sessions: 5, minutes: 30, time: '19:00' };
  return { sessions: 3, minutes: 30, time: '18:00' };
}

export function routineActivity(name: string, c: { minutes?: number; days?: number[]; quota?: number; weeklyMinutes?: number; time?: string | null; slot?: 'morning' | 'afternoon' | 'evening' }, slotDefault: Record<string, string>): RoutineActivity {
  return {
    name,
    minutes: c.minutes ?? null,
    days: c.days && c.days.length < 7 ? c.days : c.days ? ALL_DAYS : null,
    sessions: c.quota ?? null,
    weeklyMinutes: c.weeklyMinutes ?? null,
    time: c.time ?? (c.slot ? slotDefault[c.slot]! : null),
  };
}

/**
 * Rutina semanal: un hábito por actividad, en días fijos y a una hora sin choques con lo
 * que ya hay ni entre ellas. Los días libres quedan sin nada nuevo. Si existe un hábito de
 * la misma actividad, se ajusta en lugar de duplicarlo.
 */
export function buildRoutinePlan(d: RoutineDraft, env: PlanEnv): Plan {
  const { data, locale } = env;
  const allowed = ALL_DAYS.filter((x) => !d.freeDays.includes(x));
  const items: Proposal[] = [];
  const explain: TextRef[] = [];
  const warnings: TextRef[] = [];
  /** Bloques ya asignados en esta rutina, con su duración (para no solaparlas entre sí). */
  const reserved: { date: LocalDate; start: number; end: number }[] = [];
  const pool = activeHabits(data).filter((h) => h.kind === 'build');

  d.activities.forEach((a, i) => {
    const def = defaultsFor(a.name);
    let minutes = a.minutes ?? def.minutes;
    let sessions = a.sessions ?? (a.days ? a.days.length : def.sessions);
    if (a.weeklyMinutes) ({ sessions, minutes } = sessionsFor(a.weeklyMinutes, a.minutes, allowed.length));
    sessions = Math.min(sessions, allowed.length);
    const days = a.days ? a.days.filter((x) => allowed.includes(x)) : spreadDays(sessions, allowed, d.variant + i);
    if (!days.length) {
      warnings.push(tr('chat.routine.noDays', { name: a.name }));
      return;
    }

    // Hora: la pedida, o la primera sin choques (con lo existente y lo ya colocado en esta rutina).
    const candidates = a.time
      ? [a.time]
      : d.windows?.length
        ? d.windows.filter((w) => w.days.some((x) => days.includes(x))).flatMap((w) => {
            const out: string[] = [];
            for (let t = w.start; toMinutes(t) + minutes <= toMinutes(w.end); t = addMinutes(t, 30)) out.push(t);
            return out;
          })
        : [def.time, ...CANDIDATE_TIMES.filter((t) => t !== def.time)];
    // Preferencias: primero las horas de la franja preferida de esta actividad y nunca fuera de
    // los límites ("nada antes de las 8"), salvo que no quede ninguna (se avisa).
    const pref = a.time ? null : preferredSlot(env.prefs, a.name);
    const inSlot = (t: string) => (pref === 'morning' ? t < '12:00' : pref === 'afternoon' ? t >= '12:00' && t < '19:00' : pref === 'evening' ? t >= '19:00' : false);
    const prefOrdered = pref ? [...candidates.filter(inSlot), ...candidates.filter((t) => !inSlot(t))] : candidates;
    const limited = a.time ? prefOrdered : prefOrdered.filter((t) => withinLimits(env.prefs, t, minutes));
    if (!a.time && !limited.length && prefOrdered.length) warnings.push(tr('chat.prefs.noTimeInLimits', { name: a.name }));
    const times = limited.length ? limited : prefOrdered;
    if (pref) explain.push(tr('chat.prefs.usedSlotFor', { name: a.name, slot: makeT(locale)(`chat.prefs.slots.${pref}`) }));
    const shift = a.time ? 0 : d.variant % Math.max(1, times.length);
    const ordered = [...times.slice(shift), ...times.slice(0, shift)];
    const clashes = (time: string) =>
      reserved.some((r) => days.includes(isoWeekday(r.date)) && toMinutes(time) < r.end && r.start < toMinutes(time) + minutes);
    let time = ordered.find((t) => conflictCount(env, days, t, minutes) === 0 && !clashes(t)) ?? null;
    if (!time) {
      time = ordered[0] ?? def.time;
      warnings.push(tr('chat.routine.conflict', { name: a.name, time }));
    }
    for (let k = 0; k < HORIZON_DAYS; k++) {
      const date = addDays(env.ctx.today, k);
      if (days.includes(isoWeekday(date))) reserved.push({ date, start: toMinutes(time), end: toMinutes(time) + minutes });
    }

    const schedule: Extract<Proposal, { kind: 'create_habit' }>['schedule'] = days.length === 7 ? { type: 'daily' } : { type: 'weekdays', days };
    const existing = relatedHabits(a.name, pool)[0];
    if (existing) {
      items.push({ kind: 'update_habit', habitId: existing.id, habitName: existing.name, schedule, goalType: 'numeric', target: minutes, unit: 'min' });
      items.push({ kind: 'set_reminder', habitId: existing.id, habitName: existing.name, time, days: schedule.type === 'weekdays' ? days : null });
      explain.push(tr('chat.routine.adjusts', { name: existing.name, activity: a.name }));
    } else {
      items.push({ kind: 'create_habit', name: a.name, habitKind: 'build', schedule, goalType: 'numeric', target: minutes, unit: 'min', reminder: { time, days: schedule.type === 'weekdays' ? days : null } });
    }
    explain.push(
      tr(a.minutes || a.sessions || a.days || a.weeklyMinutes ? 'chat.routine.line' : 'chat.routine.lineDefault', {
        name: a.name,
        days: days.length === 7 ? makeT(locale)('assistant.freq.daily').toLocaleLowerCase() : dayNames(days, locale),
        minutes,
        time,
      }),
    );
  });

  if (d.freeDays.length) explain.push(tr('chat.plan.freeDays', { days: dayNames(d.freeDays, locale) }));
  explain.push(tr('chat.routine.noOverlap', { days: HORIZON_DAYS }));
  explain.push(tr('chat.routine.howToChange'));
  const tasks = schedulableTasks(data, env.ctx.today).length;
  if (tasks) warnings.push(tr('chat.routine.tasksLeft', { count: tasks }));
  warnings.push(tr('chat.plan.agendaScope'));
  warnings.push(tr('chat.plan.reminderNote'));
  return { items, explain, warnings };
}

// --- Semana → tareas ------------------------------------------------------------------------

/** Tareas que se pueden colocar: sin hora y vencidas, sin fecha o con fecha en los próximos 7 días. */
export function schedulableTasks(data: AppData, today: LocalDate): HabitRec[] {
  const last = addDays(today, 6);
  return openTasks(data).filter((h) => !h.dueTime && (!h.dueDate || h.dueDate <= last));
}

export function buildWeekPlan(d: WeekDraft, env: PlanEnv): Plan & { unplaced: string[] } {
  const { data, ctx, locale } = env;
  const today = ctx.today;
  const last = addDays(today, 6);
  const fmt = (x: string) => formatDay(x, locale, { weekday: 'short', day: 'numeric', month: 'short' });
  const current = nowHHMM(env.now, ctx.tz);

  const taken = new Map<LocalDate, Busy[]>();
  const busy = (date: LocalDate) => {
    if (!taken.has(date)) taken.set(date, busyOn(data, date));
    return taken.get(date)!;
  };
  let skippedBusy = 0;
  /** Primer bloque libre de 30 min en las ventanas de ese día. */
  const firstFree = (date: LocalDate): string | null => {
    const wd = isoWeekday(date);
    for (const w of d.windows) {
      if (!w.days.includes(wd)) continue;
      for (let t = w.start; toMinutes(t) + TASK_BLOCK_MINUTES <= toMinutes(w.end); t = addMinutes(t, TASK_BLOCK_MINUTES)) {
        if (date === today && t <= current) continue;
        if (conflictsIn(busy(date), t, TASK_BLOCK_MINUTES).length) {
          skippedBusy++;
          continue;
        }
        return t;
      }
    }
    return null;
  };
  const reserve = (date: LocalDate, time: string, name: string) => busy(date).push({ date, time, name });

  const tasks = schedulableTasks(data, today).filter((h) => !d.removed.includes(h.id));
  const rank = (h: HabitRec) => (h.dueDate && h.dueDate < today ? 0 : h.dueDate ? 1 : 2);
  tasks.sort((a, b) => rank(a) - rank(b) || (a.dueDate ?? '').localeCompare(b.dueDate ?? '') || a.sortKey.localeCompare(b.sortKey));

  const items: Proposal[] = [];
  const unplaced: string[] = [];
  for (const h of tasks) {
    const fixed = d.overrides[h.id];
    if (fixed) {
      items.push({ kind: 'reschedule_task', habitId: h.id, habitName: h.name, dueDate: fixed.date, dueTime: fixed.time });
      if (fixed.time) reserve(fixed.date as LocalDate, fixed.time, h.name);
      continue;
    }
    const candidates: LocalDate[] = [];
    if (h.dueDate && h.dueDate >= today) candidates.push(h.dueDate);
    else for (let i = 0; i < 7; i++) candidates.push(addDays(today, i));
    // Alternativa: repartir desde el final de la semana en lugar de lo antes posible.
    if ((d.variant ?? 0) % 2 === 1) candidates.reverse();
    let placed = false;
    for (const date of candidates) {
      const time = firstFree(date);
      if (!time) continue;
      items.push({ kind: 'reschedule_task', habitId: h.id, habitName: h.name, dueDate: date, dueTime: time });
      reserve(date, time, h.name);
      placed = true;
      break;
    }
    if (!placed) unplaced.push(h.name);
  }

  const explain: TextRef[] = [tr('chat.week.period', { from: fmt(today), to: fmt(last) })];
  for (const w of d.windows) explain.push(tr('chat.week.window', { days: w.days.length === 7 ? makeT(locale)('assistant.freq.daily') : dayNames(w.days, locale), start: w.start, end: w.end }));
  if (d.assumedAllDays) explain.push(tr('chat.week.assumedAllDays'));
  explain.push(tr('chat.week.order'));
  explain.push(tr('chat.week.block', { minutes: TASK_BLOCK_MINUTES }));
  if (skippedBusy) explain.push(tr('chat.week.avoided'));
  const habits = activeHabits(data).length;
  if (habits) explain.push(tr('chat.week.habits', { count: habits }));
  const warnings: TextRef[] = [];
  if (unplaced.length) warnings.push(tr('chat.week.unplaced', { names: unplaced.map((n) => `«${n}»`).join(', ') }));
  warnings.push(tr('chat.plan.agendaScope'));
  return { items, explain, warnings, unplaced };
}

export type { Window };
